import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import EmailTemplateService from '../../../utils/emailTemplateService';

const TemplateList = ({ 
  templates, 
  loading, 
  onTemplateSelect, 
  onTemplateToggle, 
  onTemplatePreview 
}) => {
  
  const getTemplateTypeInfo = (templateType) => {
    const options = EmailTemplateService.getTemplateTypeOptions();
    return options.find(opt => opt.value === templateType) || {
      label: templateType,
      description: 'Custom template'
    };
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'text-accent' : 'text-muted-foreground';
  };

  const getStatusIcon = (isActive) => {
    return isActive ? 'CheckCircle' : 'XCircle';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Loader2" size={20} className="animate-spin" />
            <span>Loading templates...</span>
          </div>
        </div>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="text-center">
          <Icon name="Mail" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No templates found</h3>
          <p className="text-muted-foreground mb-4">
            No email templates match your current filters.
          </p>
          <Button
            iconName="Plus"
            onClick={() => onTemplateSelect(null)}
          >
            Create New Template
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              <th className="text-left py-3 px-4 font-medium text-foreground">Template</th>
              <th className="text-left py-3 px-4 font-medium text-foreground">Type</th>
              <th className="text-left py-3 px-4 font-medium text-foreground">Status</th>
              <th className="text-left py-3 px-4 font-medium text-foreground">Last Updated</th>
              <th className="text-right py-3 px-4 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {templates.map((template) => {
              const typeInfo = getTemplateTypeInfo(template.template_type);
              
              return (
                <tr key={template.id} className="hover:bg-muted/20 transition-colors">
                  <td className="py-4 px-4">
                    <div>
                      <div className="font-medium text-foreground">{template.name}</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {typeInfo.description}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      {typeInfo.label}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={getStatusIcon(template.is_active)} 
                        size={16} 
                        className={getStatusColor(template.is_active)}
                      />
                      <span className={`text-sm font-medium ${getStatusColor(template.is_active)}`}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm text-muted-foreground">
                      {formatDate(template.updated_at)}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => onTemplatePreview(template)}
                        title="Preview template"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onTemplateSelect(template)}
                        title="Edit template"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName={template.is_active ? 'ToggleLeft' : 'ToggleRight'}
                        onClick={() => onTemplateToggle(template.template_type, !template.is_active)}
                        className={template.is_active ? 'text-accent' : 'text-muted-foreground'}
                        title={template.is_active ? 'Deactivate template' : 'Activate template'}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TemplateList;
